// Analytics page - placeholder

import React, { useState } from 'react'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Select'
import { useAuth } from '../../hooks/useAuth'

// Mock data for analytics
const mockAnalytics = {
  overview: {
    totalPosts: 156,
    totalEngagement: 2847,
    avgEngagementRate: 4.2,
    totalReach: 125000,
    growthRate: 12.5
  },
  platforms: [
    { name: 'LinkedIn', posts: 45, engagement: 1200, reach: 45000, growth: 15.2 },
    { name: 'Twitter', posts: 38, engagement: 890, reach: 32000, growth: 8.7 },
    { name: 'Facebook', posts: 42, engagement: 567, reach: 28000, growth: 5.3 },
    { name: 'Instagram', posts: 31, engagement: 190, reach: 20000, growth: 22.1 }
  ],
  topContent: [
    { title: 'AI in Modern Business', engagement: 234, reach: 8500, platform: 'LinkedIn' },
    { title: 'Digital Transformation Tips', engagement: 189, reach: 6200, platform: 'Twitter' },
    { title: 'Future of Work', engagement: 156, reach: 4800, platform: 'Facebook' },
    { title: 'Tech Innovation', engagement: 134, reach: 3900, platform: 'Instagram' }
  ],
  trends: [
    { date: '2024-01', engagement: 1200, reach: 45000 },
    { date: '2024-02', engagement: 1350, reach: 52000 },
    { date: '2024-03', engagement: 1420, reach: 58000 },
    { date: '2024-04', engagement: 1580, reach: 65000 },
    { date: '2024-05', engagement: 1720, reach: 72000 },
    { date: '2024-06', engagement: 1890, reach: 80000 }
  ]
}

const AnalyticsPage: React.FC = () => {
  const { user } = useAuth()
  const [timeRange, setTimeRange] = useState('30d')
  const [selectedPlatform, setSelectedPlatform] = useState('all')

  const getEngagementColor = (rate: number) => {
    if (rate >= 5) return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-400'
    if (rate >= 3) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-400'
    return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-400'
  }

  const getGrowthColor = (growth: number) => {
    if (growth >= 10) return 'text-green-600'
    if (growth >= 0) return 'text-yellow-600'
    return 'text-red-600'
  }

  return (
    <div className="space-y-8 fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold gradient-text">
            Analytics Dashboard
          </h1>
          <p className="text-muted-foreground mt-2">
            Track your content performance and engagement across all platforms
          </p>
        </div>
        
        <div className="flex gap-3">
          <Select
            value={timeRange}
            onValueChange={setTimeRange}
            className="w-32"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </Select>
          
          <Select
            value={selectedPlatform}
            onValueChange={setSelectedPlatform}
            className="w-40"
          >
            <option value="all">All Platforms</option>
            <option value="linkedin">LinkedIn</option>
            <option value="twitter">Twitter</option>
            <option value="facebook">Facebook</option>
            <option value="instagram">Instagram</option>
          </Select>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="dashboard-grid">
        <Card className="stats-card slide-up" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="stats-label">Total Posts</p>
              <p className="stats-value">{mockAnalytics.overview.totalPosts}</p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
        </Card>

        <Card className="stats-card slide-up" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="stats-label">Total Engagement</p>
              <p className="stats-value">{mockAnalytics.overview.totalEngagement.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
              <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
        </Card>

        <Card className="stats-card slide-up" style={{ animationDelay: '0.3s' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="stats-label">Avg. Engagement Rate</p>
              <p className="stats-value">{mockAnalytics.overview.avgEngagementRate}%</p>
            </div>
            <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </Card>

        <Card className="stats-card slide-up" style={{ animationDelay: '0.4s' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="stats-label">Total Reach</p>
              <p className="stats-value">{(mockAnalytics.overview.totalReach / 1000).toFixed(1)}K</p>
            </div>
            <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <svg className="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
          </div>
        </Card>
      </div>

      {/* Platform Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="card-modern slide-up" style={{ animationDelay: '0.5s' }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold">Platform Performance</h3>
            <Badge variant="secondary">{timeRange}</Badge>
          </div>
          
          <div className="space-y-4">
            {mockAnalytics.platforms.map((platform, index) => (
              <div key={platform.name} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                    {platform.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium">{platform.name}</p>
                    <p className="text-sm text-muted-foreground">{platform.posts} posts</p>
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="font-semibold">{platform.engagement.toLocaleString()}</p>
                  <p className={`text-sm ${getGrowthColor(platform.growth)}`}>
                    {platform.growth > 0 ? '+' : ''}{platform.growth}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="card-modern slide-up" style={{ animationDelay: '0.6s' }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold">Top Performing Content</h3>
            <Button variant="outline" size="sm" className="btn-outline">View All</Button>
          </div>
          
          <div className="space-y-4">
            {mockAnalytics.topContent.map((content, index) => (
              <div key={index} className="p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-sm line-clamp-2">{content.title}</h4>
                  <Badge variant="outline" className="text-xs">
                    {content.platform}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    {content.engagement}
                  </span>
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    {(content.reach / 1000).toFixed(1)}K
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Engagement Trend Chart */}
      <Card className="card-modern slide-up" style={{ animationDelay: '0.7s' }}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold">Engagement Trend</h3>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="btn-outline">Export</Button>
            <Button variant="outline" size="sm" className="btn-outline">Share</Button>
          </div>
        </div>
        
        <div className="h-64 flex items-center justify-center bg-muted/20 rounded-lg">
          <div className="text-center">
            <svg className="w-16 h-16 text-muted-foreground mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p className="text-muted-foreground">Interactive chart coming soon</p>
            <p className="text-sm text-muted-foreground">Engagement trends over time</p>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default AnalyticsPage